import { useEffect, useState } from 'react';
import serverErrorHandler from 'services/serverError.service';
import useUserStore from 'store/useUserStore';
import { SclaApplication } from '../types';
import getSclaApp from '../api/getSclaApp';
import startSclaApp from '../api/startSclaApp';
import chooseSclaApplication from '../utils/chooseSclaApplication';

export default function useSclaApplication(
  userId: string,
  cohortYear: string,
  autoStart = true,
  persist = true,
) {
  const storedApplication = useUserStore(s => s.application);
  const [localApplication, setLocalApplication] = useState<SclaApplication | null>(
    null,
  );

  const setApplication = (app: SclaApplication) => {
    const state = useUserStore.getState();
    const next = chooseSclaApplication(state.application, app, state.user?._id);
    if (!next) return;
    setLocalApplication(next);
    if (persist) state.setUserApplication(next);
  };
  const [isDoneLoading, setIsDoneLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const storedOwner = storedApplication?.user as { _id?: string } | string | undefined;
  const storedUserId = typeof storedOwner === 'string' ? storedOwner : storedOwner?._id;
  const storedMatchesUser = !storedUserId || storedUserId === userId;
  let application = localApplication;
  if (persist && storedMatchesUser && storedApplication?.cohortYear === cohortYear) {
    application = storedApplication;
  }

  useEffect(() => {
    if (!userId || !cohortYear) return undefined;

    let mounted = true;

    async function load() {
      try {
        setIsDoneLoading(false);
        const app = await getSclaApp(cohortYear);

        if (!mounted) return;
        if (app) {
          setApplication(app);
        } else if (autoStart) {
          const newApp = await startSclaApp(cohortYear);
          if (mounted) setApplication(newApp);
        }
        if (mounted) setIsDoneLoading(true);
      } catch (e) {
        if (!mounted) return;
        setIsDoneLoading(true);
        serverErrorHandler(e);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [userId, cohortYear, autoStart, persist]);

  const startApplication = async () => {
    try {
      setIsStarting(true);
      const app = await startSclaApp(cohortYear);
      setApplication(app);
      setIsStarting(false);
    } catch (e) {
      setIsStarting(false);
      serverErrorHandler(e);
    }
  };

  return {
    application,
    isDoneLoading,
    isStarting,
    startApplication,
    setApplication,
  };
}
